import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';
import { CognitoAuthenticatedUser, NotificationDto, UserDto } from "../../models";
import { ApiHttpService } from "../../services/api-http.service";
import { ActivatedRoute, Router } from "@angular/router";
import { DataStorageService } from "../../services/data-storage.service";
import { AccountService } from "../../services/account.service";

@Component({
  selector: 'cpf-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class HeaderComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  notificationContainerOpen: boolean = false;
  copiedNotificationId: string | null = null;
  user?: UserDto;
  cognitoUser?: CognitoAuthenticatedUser | null;
  notifications: NotificationDto[] = [];
  isDesktop: boolean = false;
  avatarUrl?: string;

  constructor(private apiHttpService: ApiHttpService,
              private route: ActivatedRoute,
              private dataStorageService: DataStorageService,
              private accountService: AccountService,
              private router: Router
  ) {
    this.route.url.subscribe(urlSegments => {
      this.isDesktop = urlSegments.some(segment => segment.path === 'desktop');
    });

    this.dataStorageService.user$.subscribe(async user => {
      if (user && user.avatarImgFilename) {
        this.avatarUrl = (await this.apiHttpService.getUrlForProfilePicture()).Location;
      }
    })

    this.user = this.dataStorageService.user;
    this.cognitoUser = this.accountService.user;
  }

  async ngOnInit(): Promise<void> {
    const notifications = await this.apiHttpService.getUserNotifications();
    this.notifications = notifications.filter(notification => notification.status === 'new').sort((a, b) => new Date(a.dateCreated) > new Date(b.dateCreated) ? -1 : 1);
  }

  haveUnreadNotifications() {
    return this.notifications.some(notification => notification.status === 'new');
  }

  toggleNotificationContainer() {
    this.notificationContainerOpen = !this.notificationContainerOpen;
  }

  getNotificationStyle(notification: NotificationDto) {
    if (this.isMeikoVoucher(notification)) {
      return 'notification-voucher';
    }
    switch (notification.styleInfo) {
      case 'error':
        return 'notification-error';
      case 'yellow':
        return 'notification-pay';
      case 'green':
        return 'notification-renew';
      case 'blue':
        return 'notification-refund';
      case 'info':
        return 'notification-info';
    }
    return 'notification-pay';
  }

  getNotificationTranslationKey(notification: NotificationDto, title: string) {
    if (notification.type === 'meiko_voucher') {
      return `header.notifications.meiko-voucher.${title}`;
    }
    switch (notification.styleInfo) {
      case 'error':
        return `header.notifications.payment-error.${title}`;
      case 'yellow':
        return `header.notifications.payment-soon.${title}`;
      case 'green':
        return `header.notifications.renewal-soon.${title}`;
      case 'blue':
        return `header.notifications.refund.${title}`;
      case 'info':
        return `header.notifications.info.${title}`;
    }
    return `header.notifications.payment-error.${title}`;
  }

  isMeikoVoucher(notification: NotificationDto): boolean {
    return notification.type === 'meiko_voucher';
  }

  onCouponCopied(notificationId: string): void {
    this.copiedNotificationId = notificationId;
    timer(2000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.copiedNotificationId = null;
    });
  }

  async markNotificationAsRead(notification: NotificationDto) {
    notification.status = 'read';
    this.notifications = this.notifications.filter(n => n.id !== notification.id);
    const notificationResponse = await this.apiHttpService.markNotificationAsRead(notification.id);
  }
}
